import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, History, Layers } from 'lucide-react'

import type { SortingLotHistoryEntry } from '@/lib/api/sorting-lot-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { HistoryEntryCard } from '@/components/common/HistoryEntryCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { WarehouseNoteStatusBadge } from '@/components/warehouse/note-status-badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useSortingLot,
    useSortingLotHistory,
} from '@/hooks/use-sorting-lot-query'
import { formatDateTimeVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sorting-warehouse/lots/$id',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const { data, isLoading } = useSortingLot(id)
    const historyQuery = useSortingLotHistory(id)
    const lot = data?.data
    const history = historyQuery.data?.pages.flatMap((p) => p.data ?? []) ?? []

    if (isLoading) return <div className="p-6">{m.sl_lots_loading()}</div>
    if (!lot) return <div className="p-6">{m.sl_lot_not_found()}</div>

    return (
        <div className="space-y-4 page-pad">
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <PageBackButton asChild>
                            <Link
                                to="/sorting-warehouse/lots"
                                search={{ page: 1 }}
                            >
                                <ArrowLeft className="size-4" />
                            </Link>
                        </PageBackButton>
                        <Layers className="size-5" />
                        <CardTitle className="font-mono text-base md:text-2xl">
                            {lot.code}
                        </CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="grid gap-3 grid-cols-2">
                    <Field
                        label={m.sl_lot_grade()}
                        value={lot.grade_name ?? '—'}
                    />
                    <div className="col-span-2">
                        <Field
                            label={m.sl_lot_created_at()}
                            value={formatDateTimeVN(lot.created_at)}
                        />
                    </div>
                    <div className="col-span-2 rounded-md bg-primary/10 p-3 text-center">
                        <p className="text-xs text-muted-foreground">
                            {m.sl_lot_available()}
                        </p>
                        <p className="text-2xl font-semibold tabular-nums text-primary md:text-xl">
                            {m.sl_lot_kg_value({
                                qty: formatQty(lot.quantity_available),
                            })}
                        </p>
                    </div>
                    {lot.created_by_name && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_lot_created_by()}
                                value={lot.created_by_name}
                            />
                        </div>
                    )}
                    {lot.notes && (
                        <div className="col-span-2">
                            <Field label={m.sl_lot_notes()} value={lot.notes} />
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <History className="size-5" />
                        <CardTitle>{m.sl_lot_history_title()}</CardTitle>
                    </div>
                </CardHeader>
                <CardContent>
                    {history.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-2">
                            {m.sl_lot_history_empty()}
                        </p>
                    ) : (
                        <>
                            {/* Mobile: card list */}
                            <div className="space-y-2 md:hidden">
                                {history.map((h, idx) => (
                                    <HistoryEntryCard
                                        key={`${h.note_id}-${idx}`}
                                        type={h.note_type}
                                        createdAt={h.created_at}
                                        noteLink={<NoteLink entry={h} />}
                                        statusBadge={
                                            <WarehouseNoteStatusBadge
                                                status={h.status}
                                            />
                                        }
                                        reason={reasonLabel(h)}
                                        notes={h.notes}
                                        quantity={h.quantity}
                                        unit="kg"
                                        createdBy={h.created_by_name}
                                    />
                                ))}
                            </div>

                            {/* Desktop: table */}
                            <div className="hidden md:block rounded-md border overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>
                                                {m.sl_lot_history_col_date()}
                                            </TableHead>
                                            <TableHead>
                                                {m.sl_lot_history_col_type()}
                                            </TableHead>
                                            <TableHead>
                                                {m.sl_lot_history_col_note()}
                                            </TableHead>
                                            <TableHead>
                                                {m.sl_lot_history_col_status()}
                                            </TableHead>
                                            <TableHead>
                                                {m.sl_lot_history_col_reason()}
                                            </TableHead>
                                            <TableHead>
                                                {m.sl_lot_history_col_notes()}
                                            </TableHead>
                                            <TableHead className="text-right">
                                                {m.sl_lot_history_col_quantity()}
                                            </TableHead>
                                            <TableHead>
                                                {m.sl_lot_history_col_created_by()}
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {history.map((h, idx) => (
                                            <TableRow
                                                key={`${h.note_id}-${idx}`}
                                            >
                                                <TableCell className="text-sm text-muted-foreground">
                                                    {formatDateTimeVN(
                                                        h.created_at,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <TypeBadge
                                                        type={h.note_type}
                                                    />
                                                </TableCell>
                                                <TableCell className="font-mono text-sm">
                                                    <NoteLink entry={h} />
                                                </TableCell>
                                                <TableCell>
                                                    <WarehouseNoteStatusBadge
                                                        status={h.status}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {reasonLabel(h)}
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                                                    {h.notes ?? '—'}
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums">
                                                    {h.note_type === 'in'
                                                        ? '+'
                                                        : '−'}
                                                    {formatQty(h.quantity)}
                                                </TableCell>
                                                <TableCell>
                                                    {h.created_by_name ?? '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </>
                    )}
                    {historyQuery.hasNextPage && (
                        <Button
                            variant="outline"
                            className="w-full h-11 mt-3"
                            disabled={historyQuery.isFetchingNextPage}
                            onClick={() => void historyQuery.fetchNextPage()}
                        >
                            {historyQuery.isFetchingNextPage
                                ? m.list_loading_more()
                                : m.list_load_more()}
                        </Button>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

function TypeBadge({ type }: { type: 'in' | 'out' }) {
    if (type === 'in') {
        return (
            <Badge className="bg-blue-100 text-blue-700">
                {m.sl_lot_history_type_in()}
            </Badge>
        )
    }
    return (
        <Badge className="bg-orange-100 text-orange-700">
            {m.sl_lot_history_type_out()}
        </Badge>
    )
}

function NoteLink({ entry }: { entry: SortingLotHistoryEntry }) {
    if (entry.note_type === 'in') {
        return (
            <Link
                to="/sorting-warehouse/stock-in-notes/$id"
                params={{ id: entry.note_id }}
                className="text-primary hover:underline"
            >
                {entry.note_code}
            </Link>
        )
    }
    return (
        <Link
            to="/sorting-warehouse/stock-out-notes/$id"
            params={{ id: entry.note_id }}
            className="text-primary hover:underline"
        >
            {entry.note_code}
        </Link>
    )
}

function reasonLabel(h: SortingLotHistoryEntry): string {
    if (h.note_type === 'in') {
        return h.reason === 'from_purchase'
            ? m.sl_in_reason_from_purchase()
            : m.sl_in_reason_adjustment()
    }
    if (h.reason === 'for_scenting') return m.sl_out_reason_for_scenting()
    if (h.reason === 'discard') return m.sl_out_reason_discard()
    return m.sl_out_reason_adjustment()
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-base font-medium md:text-sm">{value}</p>
        </div>
    )
}
